import { ErrorAlert } from '@components/Alerts/ErrorAlert';
import { SuccessAlert } from '@components/Alerts/SuccessAlert';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { Button, ButtonType } from '@components/Button';
import { Container } from '@components/Container';
import { Input } from '@components/FormComponents';
import { StandardLayout } from '@components/Layouts/StandardLayout';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { log } from '@lib/logs';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { type ReactElement, type ReactNode, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

export default function ResetPasswordPage(): ReactElement {
  const [error, setError] = useState(false);
  const [errorText, setErrorText] = useState<ReactNode>();
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const router = useRouter();

  const rhfMethods = useForm<any>({
    mode: 'onTouched',
    defaultValues: {
      password: '',
      confirm: '',
    },
  });

  const handleSubmit = async (e: {
    password: string;
    confirm: string;
    code: string;
  }) => {
    try {
      setSubmitting(true);
      const response = await fetch('api/customer/update-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          password: e.password,
          confirm: e.confirm,
          code: e.code,
          query: router.query.v,
        }),
      });

      if (response.status === 200) {
        //   done it
        setSuccess(true);
        setTimeout(() => {
          router.push('/login');
        }, 5000);
      }
      if (response.status === 400) {
        //   error, password issues (validation, doesn't match, etc)
        setError(true);
        const msg = await response.json();
        setErrorText(<Paragraph>{msg.error.message.join('. ')}</Paragraph>);
      }
      if (response.status === 401) {
        //   error, token expired
        setError(true);
        setErrorText(
          <Paragraph>
            We are unable to validate that email and code. Please submit a new
            request or contact Customer Service.
          </Paragraph>
        );
      }
      if (response.status === 403) {
        //   error, code invalid
        setError(true);
        setErrorText(
          <Paragraph>
            The code supplied is invalid, please submit a new request
          </Paragraph>
        );
      }
      if (response.status === 429) {
        //   error, rate limited
        setError(true);
        setErrorText(
          <Paragraph>
            Too many requests have been made, please try again in 5 minutes.
          </Paragraph>
        );
      }
      if (response.status === 500) {
        //   error, JWT invalid
        setError(true);
        setErrorText(<Paragraph>Unexpected error</Paragraph>);
      }
    } catch (err) {
      log({ error: err });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SuccessAlert
        message="Great! You've successfully reset your password and can login now. Sit tight, we're redirecting you back to the login in page now."
        setShow={setSuccess}
        show={success}
        title="Password reset"
      />
      <ErrorAlert setShow={setError} show={error}>
        {errorText}
      </ErrorAlert>
      <Head>
        <title>Bulk Nutrients - Our Form to Reset Forgotten Passwords</title>
        <meta
          content="Bulk Nutrients - Our Form to Reset Forgotten Passwords"
          name="title"
        />
        <meta
          content="Forgot your password? No worries! Reset it quickly and securely with Bulk Nutrients' password recovery process. Get back into your Bulk account today!"
          name="description"
        />
        <link
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}/reset-password`}
          rel="canonical"
        />
      </Head>
      <Container>
        <Breadcrumbs
          crumbs={[
            { title: 'Home', path: '/' },
            { title: 'Reset Password', path: '/reset-password' },
          ]}
        />
      </Container>
      {/** biome-ignore lint/correctness/useUniqueElementIds: we just the main-content with the skip to content button */}
      <Container
        as="main"
        className="grid-cols-[1fr_min(var(--width-narrow-page),calc(100%-16px))_1fr]"
        id="main-content"
      >
        <div className="justify-self-center">
          <h1>Change Password</h1>
          <Paragraph className="max-w-narrow-page">
            Passwords must be at least 12 characters long, with an uppercase
            letter, a lowercase letter, a number, and optionally any of these
            special characters: @$!%*#?&()
          </Paragraph>
          <FormProvider {...rhfMethods}>
            <form
              className="flex flex-col items-start gap-2.5"
              onSubmit={rhfMethods.handleSubmit(handleSubmit)}
            >
              <Input
                description="You should have received an email containing this code"
                label="Verification Code"
                name="code"
                required
                type="text"
              />
              <Input
                className="w-[300px]"
                description="Passwords must be at least 12 characters long, with an uppercase letter, a lowercase letter, a number, and optionally any of these special characters: @$!%*#?&()"
                label="New Password"
                name="password"
                required
                type="password"
              />
              <Input
                className="w-[300px]"
                label="Confirm Password"
                name="confirm"
                required
                type="password"
                validate={(_, values) =>
                  values.password.trim() === values.confirm.trim()
                    ? true
                    : 'Passwords do not match'
                }
              />
              <Button
                buttonStyle="primary"
                className="h-10"
                disabled={submitting}
                type={ButtonType.submit}
              >
                Continue
              </Button>
            </form>
          </FormProvider>
        </div>
      </Container>
    </>
  );
}

ResetPasswordPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};
